import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { DealBusinessTypeApiService } from '../services/deal-business-type-api.service';
import { DealBusinessTypeRequest } from '../models/deal-business-type.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { PreventSpecialCharactersDirective } from '../../../shared/directives/prevent-special-characters.directive';
import { PreventLongNamesDirective } from '../../../shared/directives/prevent-long-names.directive';
import { PreventLongDescriptionsDirective } from '../../../shared/directives/prevent-long-descriptions.directive';
import { ValidatorFormsInformation } from '../../../shared/utils/validator-forms-information.utils';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-deal-business-type-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    PreventSpecialCharactersDirective,
    PreventLongNamesDirective,
    PreventLongDescriptionsDirective,
  ],
  templateUrl: './deal-business-type-form.component.html',
})
export class DealBusinessTypeFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;

  // Form fields
  name = '';
  private originalName = '';
  description = '';
  isActive = true;

  constructor(
    private api: DealBusinessTypeApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.itemId = +id;
      this.cargar(this.itemId);
    }
  }

  setError(message: string) {
    this.error = message;
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';

    const businessId = this.authService.getCurrentUser()?.businessId;
    if (!businessId) {
      this.error = 'No se pudo obtener el negocio del usuario';
      this.loading = false;
      return;
    }

    this.api.getById(id, businessId).subscribe({
      next: (item) => {
        this.name = item.name;
        this.originalName = item.name;
        this.description = item.description || '';
        this.isActive = item.isActive;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el tipo de negocio';
        this.loading = false;
      },
    });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Tipo de Negocio' : 'Nuevo Tipo de Negocio';
  }

  guardar(): void {
    const nameValidation = ValidatorFormsInformation.validateText(
      this.name,
      'nombre',
      30,
    );
    if (nameValidation) {
      this.error = nameValidation;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }

    const descriptionValidation = ValidatorFormsInformation.validateText(
      this.description,
      'descripción',
      500,
    );
    if (descriptionValidation) {
      this.error = descriptionValidation;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }

    const businessId = this.authService.getCurrentUser()?.businessId;
    if (!businessId) {
      this.error = 'No se pudo obtener el negocio del usuario';
      this.toast.error({ title: 'Error', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';

    const request: DealBusinessTypeRequest = {
      name: this.name.trim(),
      description: this.description.trim() || undefined,
      isActive: this.isActive,
      businessId,
    };

    if (this.isEditing && this.itemId) {
      this.api.update(this.itemId, request).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/config-deals/tipos-negocio']);
          this.toast.updated('Tipo de negocio',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
        },
        error: (err) => {
          this.saving = false;
          this.error =
            err.error?.message || 'Error al actualizar el tipo de negocio';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    } else {
      this.api.create(request).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/config-deals/tipos-negocio']);
          this.toast.created('Tipo de negocio', `Se creó "${this.name.trim()}" correctamente.`);
        },
        error: (err) => {
          this.saving = false;
          this.error =
            err.error?.message || 'Error al crear el tipo de negocio';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-deals/tipos-negocio']);
  }
}
